import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ResourceKind } from '@displaycms/contracts';
import { useSignedMediaUrl, invalidateSignedUrl } from './useSignedMediaUrl';

type Size = 'thumb' | 'inline' | 'full';

type ResourceSource = {
  source: 'resource';
  resourceVersionId: string;
  kind: ResourceKind;
};
type FileSource = {
  source: 'file';
  file: File;
  kind: ResourceKind;
};
export type MediaPreviewProps = (ResourceSource | FileSource) & {
  size?: Size;
  className?: string;
};

const SIZE_CLASS: Record<Size, string> = {
  thumb: 'h-12 w-12',
  inline: 'max-h-48 w-full',
  full: 'max-h-[60vh] w-full',
};

function isDynamicKind(kind: ResourceKind): boolean {
  return kind === 'rss_feed' || kind === 'web_page';
}

/**
 * Reusable, kind-dispatched media preview used on every admin surface.
 *
 * - `source: 'file'` previews a locally-selected File via an object URL (no
 *   network) — for the upload form before submit.
 * - `source: 'resource'` fetches the bytes via the existing state-agnostic
 *   admin signed-url (so draft/pending assets preview too) and renders the
 *   element matching the kind. Dynamic kinds (rss_feed/web_page) have no signed
 *   bytes URL and render a kind badge instead.
 *
 * Never blocks the surface: load → spinner, error → "Aperçu indisponible".
 */
export function MediaPreview(props: MediaPreviewProps): JSX.Element {
  const { kind, size = 'inline', className } = props;
  const { t } = useTranslation('resources');
  const dynamic = isDynamicKind(kind);

  // Local file source → object URL.
  const file = props.source === 'file' ? props.file : null;
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!file) {
      setFileUrl(null);
      return;
    }
    const u = URL.createObjectURL(file);
    setFileUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);

  // Resource source (static kinds only) → signed URL.
  const resourceVersionId =
    props.source === 'resource' && !dynamic ? props.resourceVersionId : null;
  const signed = useSignedMediaUrl(resourceVersionId);

  // A signed URL can resolve yet the bytes still fail to load — a TTL-expired
  // URL handed out near its boundary, a 404, or a network blip. Track that so
  // we degrade to the same graceful placeholder instead of a broken-image icon,
  // and drop the dead cache entry so the next mount re-signs.
  const [loadFailed, setLoadFailed] = useState(false);
  useEffect(() => {
    setLoadFailed(false); // reset when the target asset changes
  }, [resourceVersionId]);
  const handleBytesError = (): void => {
    if (resourceVersionId) invalidateSignedUrl(resourceVersionId);
    setLoadFailed(true);
  };

  const box = `inline-flex items-center justify-center overflow-hidden rounded bg-muted ${SIZE_CLASS[size]} ${className ?? ''}`;

  // Dynamic kinds: badge (no bytes preview).
  if (dynamic) {
    return (
      <div className={box} data-testid="media-preview-dynamic-icon" role="img" aria-label={kind}>
        <span className="text-[10px] font-semibold uppercase text-muted-foreground">
          {kind === 'rss_feed' ? 'RSS' : 'WEB'}
        </span>
      </div>
    );
  }

  if (props.source === 'resource' && (signed.error || loadFailed)) {
    return (
      <div className={box} data-testid="media-preview-placeholder" role="img" aria-label={t('preview.unavailable')}>
        <span className="px-1 text-center text-[10px] text-muted-foreground">{t('preview.unavailable')}</span>
      </div>
    );
  }

  const src = props.source === 'file' ? fileUrl : signed.url;
  if (!src) {
    return <div className={box} data-testid="media-preview-loading" aria-busy="true" />;
  }

  if (kind === 'image') {
    return (
      <img
        src={src}
        alt={t('preview.alt')}
        data-testid="media-preview-image"
        onError={props.source === 'resource' ? handleBytesError : undefined}
        className={`${SIZE_CLASS[size]} object-contain ${className ?? ''}`}
      />
    );
  }
  if (kind === 'video') {
    return (
      <video
        src={src}
        preload="metadata"
        muted
        playsInline
        controls={size !== 'thumb'}
        data-testid="media-preview-video"
        onError={props.source === 'resource' ? handleBytesError : undefined}
        className={`${SIZE_CLASS[size]} object-contain bg-black ${className ?? ''}`}
      />
    );
  }
  // audio
  if (size === 'thumb') {
    return (
      <div className={box} data-testid="media-preview-audio-icon" role="img" aria-label={t('preview.alt')}>
        <span className="text-[10px] font-semibold uppercase text-muted-foreground">AUD</span>
      </div>
    );
  }
  return <audio src={src} controls data-testid="media-preview-audio" className={className} />;
}

export default MediaPreview;
